import { apiRequest, apiRoutes } from '../../api';
import React, { useState, useEffect, useRef } from 'react';
import './PropertyWizard.css';

// Details Step Component
// establish_date is stored as a Shamsi (Persian) integer year (e.g. 1400) in the backend.
// We display and submit it as-is — no Gregorian conversion needed.
function DetailsStep({ onSubmit, initialData = {}, onPreviousStep }) {
  const [area, setArea] = useState(initialData.area?.toString() || '');
  // shamsiYear holds the display value and what we submit — it's already the Persian year integer
  const [shamsiYear, setShamsiYear] = useState(
    initialData.establish_date ? initialData.establish_date.toString() : ''
  );
  const [selectedUsage, setSelectedUsage] = useState(initialData.usage || '');
  const [selectedPropertyType, setSelectedPropertyType] = useState(initialData.property_type || '');
  const [availableAmenities, setAvailableAmenities] = useState([]);
  const [selectedAmenityIds, setSelectedAmenityIds] = useState([]);
  const [amenityNotes, setAmenityNotes] = useState({});
  const [visibleAmenityNotes, setVisibleAmenityNotes] = useState({});
  const [amenityValues, setAmenityValues] = useState({});
  const [loadingAmenities, setLoadingAmenities] = useState(false);
  const [loadingPropertyTypes, setLoadingPropertyTypes] = useState(false);
  const [loadingUsages, setLoadingUsages] = useState(false);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [usages, setUsages] = useState([]);
  // Prevents overwriting user edits when amenities re-fetch
  const amenitiesRestoredRef = useRef(false);

  // Update form when initialData changes (navigating back or initial load)
  useEffect(() => {
    setArea(initialData.area?.toString() || '');
    setShamsiYear(initialData.establish_date ? initialData.establish_date.toString() : '');
    setSelectedUsage(initialData.usage || '');
    setSelectedPropertyType(initialData.property_type || '');
    setVisibleAmenityNotes({});
    // Reset so amenities get restored again with fresh initialData
    amenitiesRestoredRef.current = false;
  }, [initialData]);

  // Fetch property types
  useEffect(() => {
    const fetchPropertyTypes = async () => {
      setLoadingPropertyTypes(true);
      try {
        const response = await apiRequest(apiRoutes.properties.propertyTypes, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setPropertyTypes(data);
        }
      } catch (error) {

      } finally {
        setLoadingPropertyTypes(false);
      }
    };
    fetchPropertyTypes();
  }, []);

  // Fetch usages
  useEffect(() => {
    const fetchUsages = async () => {
      setLoadingUsages(true);
      try {
        const response = await apiRequest(apiRoutes.properties.usages, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUsages(data);
        }
      } catch (error) {

      } finally {
        setLoadingUsages(false);
      }
    };
    fetchUsages();
  }, []);

  // Fetch amenities whenever property type changes
  useEffect(() => {
    if (!selectedPropertyType) {
      setAvailableAmenities([]);
      return;
    }

    const fetchAmenities = async () => {
      setLoadingAmenities(true);
      try {
        const response = await apiRequest(
          apiRoutes.properties.amenitiesForWizard(selectedPropertyType),
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } }
        );

        if (response.ok) {
          const data = await response.json();
          setAvailableAmenities(data);

          // Restore saved amenities from initialData — only once per initialData load
          if (!amenitiesRestoredRef.current && Array.isArray(initialData.amenities) && initialData.amenities.length > 0) {
            amenitiesRestoredRef.current = true;

            const ids = initialData.amenities
              .map(a => (typeof a === 'object' ? (a.amenity ?? a.id) : a))
              .filter(id => id != null);

            setSelectedAmenityIds(ids);

            const notes = {};
            const values = {};
            initialData.amenities.forEach(amenity => {
              if (typeof amenity === 'object' && amenity.amenity != null) {
                if (amenity.notes) notes[amenity.amenity] = amenity.notes;
                if (amenity.value !== undefined && amenity.value !== null) {
                  values[amenity.amenity] = amenity.value;
                }
              }
            });
            setAmenityNotes(notes);
            setVisibleAmenityNotes(
              Object.keys(notes).reduce((visible, amenityId) => {
                visible[amenityId] = true;
                return visible;
              }, {})
            );
            setAmenityValues(values);
          }
        } else {

        }
      } catch (error) {

      } finally {
        setLoadingAmenities(false);
      }
    };
    fetchAmenities();
  }, [selectedPropertyType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e) => {
    e.preventDefault();

    const yearInt = shamsiYear ? parseInt(shamsiYear, 10) : null;

    const stepData = {
      area: parseFloat(area),
      // Send Shamsi year integer directly — backend model is PositiveSmallIntegerField
      establish_date: yearInt,
      usage: selectedUsage,
      property_type: selectedPropertyType,
      amenities: selectedAmenityIds.map(id => ({
        amenity: id,
        notes: amenityNotes[id] || '',
        value: amenityValues[id] !== undefined ? amenityValues[id] : null
      }))
    };

    onSubmit(stepData);
  };

  // Auto-select numerical/categorical/text amenities when user enters data
  const handleValueChange = (amenityId, value) => {
    setAmenityValues(prev => ({ ...prev, [amenityId]: value }));
    if (!selectedAmenityIds.includes(amenityId) && value !== null && value !== '') {
      setSelectedAmenityIds(prev => [...prev, amenityId]);
    }
  };

  const handleNoteChange = (amenityId, note) => {
    setAmenityNotes(prev => ({ ...prev, [amenityId]: note }));
    if (!selectedAmenityIds.includes(amenityId) && note.trim() !== '') {
      setSelectedAmenityIds(prev => [...prev, amenityId]);
    }
  };

  const handleNoteVisibilityChange = (amenityId, isVisible) => {
    setVisibleAmenityNotes(prev => ({ ...prev, [amenityId]: isVisible }));

    if (!isVisible) {
      setAmenityNotes(prev => ({ ...prev, [amenityId]: '' }));
    }
  };

  // Render value input based on amenity value type
  const renderValueInput = (amenity) => {
    const amenityId = amenity.id;
    const currentValue = amenityValues[amenityId];

    switch (amenity.value_type) {
      case 'boolean':
        return (
          <div className="amenity-toggle-container">
            <div
              className={`toggle-switch-mini ${currentValue === true ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                const newValue = currentValue !== true;
                setAmenityValues(prev => ({ ...prev, [amenityId]: newValue }));
                if (!selectedAmenityIds.includes(amenityId)) {
                  setSelectedAmenityIds(prev => [...prev, amenityId]);
                }
              }}
            >
              <div className="toggle-slider-mini"></div>
            </div>
          </div>
        );

      case 'numerical':
        return (
          <input
            type="number"
            step="1"
            min={amenity.min_value || undefined}
            max={amenity.max_value || undefined}
            value={currentValue ?? ''}
            onChange={(e) => {
              const value = e.target.value === '' ? null : parseFloat(e.target.value);
              handleValueChange(amenityId, value);
            }}
            onFocus={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
            placeholder="مقدار"
            className="amenity-input-mini"
          />
        );

      case 'categorical': {
        const options = amenity.categorical_options || {};
        const hasOptions = Object.keys(options).length > 0;

        if (hasOptions) {
          return (
            <select
              value={currentValue ?? ''}
              onChange={(e) => {
                const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
                handleValueChange(amenityId, value);
              }}
              onFocus={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
              className="amenity-select-mini"
            >
              <option value="">انتخاب</option>
              {Object.entries(options).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          );
        }
        return (
          <input
            type="number"
            step="1"
            min={amenity.min_value || 1}
            max={amenity.max_value || 5}
            value={currentValue ?? ''}
            onChange={(e) => {
              const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
              handleValueChange(amenityId, value);
            }}
            placeholder="امتیاز"
            className="amenity-input-mini"
            onFocus={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
          />
        );
      }

      case 'text':
        return (
          <input
            type="text"
            value={currentValue ?? ''}
            onChange={(e) => {
              const value = e.target.value === '' ? null : e.target.value;
              handleValueChange(amenityId, value);
            }}
            placeholder="توضیحات"
            className="amenity-input-mini"
            onFocus={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
          />
        );

      default:
        return null;
    }
  };

  // Group amenities by value type
  const groupAmenitiesByType = () => {
    const grouped = { boolean: [], numerical: [], categorical: [], text: [] };
    availableAmenities.forEach(amenity => {
      if (grouped[amenity.value_type]) {
        grouped[amenity.value_type].push(amenity);
      }
    });
    return grouped;
  };

  return (
    <div className="details-step">
      <form onSubmit={handleSubmit}>
        <div className="step-header">
          <h3>جزئیات ملک</h3>
        </div>

        {/* Basic Property Information */}
        <div className="basic-info-section compact">
          <h4>اطلاعات پایه</h4>

          <div className="basic-info-grid compact-grid">
            <div className="form-group compact">
              <label>نوع ملک</label>
              <select
                value={selectedPropertyType}
                onChange={e => {
                  setSelectedPropertyType(e.target.value);
                  setSelectedAmenityIds([]);
                  setAmenityNotes({});
                  setVisibleAmenityNotes({});
                  setAmenityValues({});
                  amenitiesRestoredRef.current = false;
                }}
                required
                disabled={loadingPropertyTypes}
                className="form-select"
              >
                <option value="">{loadingPropertyTypes ? '⏳ بارگذاری...' : 'انتخاب کنید'}</option>
                {propertyTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group compact">
              <label>کاربری</label>
              <select
                value={selectedUsage}
                onChange={e => setSelectedUsage(e.target.value)}
                required
                disabled={loadingUsages}
                className="form-select"
              >
                <option value="">{loadingUsages ? '⏳ بارگذاری...' : 'انتخاب کنید'}</option>
                {usages.map(usage => (
                  <option key={usage.id} value={usage.id}>{usage.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group compact">
              <label>مساحت (م²)</label>
              <input
                type="number"
                step="1"
                value={area}
                onChange={e => setArea(e.target.value)}
                required
                className="form-input"
                placeholder="۱۲۰"
              />
            </div>

            <div className="form-group compact">
              <label>سال ساخت (شمسی)</label>
              <input
                type="text"
                value={shamsiYear}
                onChange={e => {
                  const val = e.target.value;
                  if (val === '' || /^\d+$/.test(val)) {
                    setShamsiYear(val);
                  }
                }}
                onKeyPress={e => {
                  if (!/[\d]/.test(e.key)) e.preventDefault();
                }}
                required
                placeholder="۱۴۰۰"
                className="form-input"
              />
            </div>
          </div>
        </div>

        {/* Amenities Section */}
        <div className="amenities-section unified-compact">
          <h4>امکانات ملک</h4>

          {!selectedPropertyType ? (
            <div className="no-property-type">
              ابتدا نوع ملک را انتخاب کنید
            </div>
          ) : loadingAmenities ? (
            <div className="loading-amenities">
              در حال بارگذاری امکانات...
            </div>
          ) : availableAmenities.length === 0 ? (
            <div className="no-amenities">
              هیچ امکاناتی برای این نوع ملک یافت نشد
            </div>
          ) : (() => {
            const grouped = groupAmenitiesByType();
            return (
              <div className="amenities-unified-grid">
                {grouped.boolean.map(amenity => (
                  <div key={amenity.id} className="amenity-row boolean-row">
                    <span className="amenity-label">{amenity.description}</span>
                    {renderValueInput(amenity)}
                  </div>
                ))}

                {grouped.boolean.length > 0 &&
                  (grouped.numerical.length > 0 || grouped.categorical.length > 0 || grouped.text.length > 0) && (
                    <div className="amenity-separator"></div>
                  )}

                {[...grouped.numerical, ...grouped.categorical, ...grouped.text].map(amenity => (
                  <div key={amenity.id} className="amenity-row value-row">
                    <span className="amenity-label">{amenity.description}</span>
                    <div className="amenity-inputs-row">
                      {renderValueInput(amenity)}
                      <label className="amenity-note-toggle" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={Boolean(visibleAmenityNotes[amenity.id])}
                          onChange={e => handleNoteVisibilityChange(amenity.id, e.target.checked)}
                        />
                        <span>یادداشت</span>
                      </label>
                      {visibleAmenityNotes[amenity.id] && (
                        <input
                          type="text"
                          value={amenityNotes[amenity.id] || ''}
                          onChange={e => handleNoteChange(amenity.id, e.target.value)}
                          placeholder="یادداشت را وارد کنید"
                          className="amenity-note-mini"
                          onClick={e => e.stopPropagation()}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        <div className="navigation-buttons">
          <button type="button" onClick={handleSubmit} className="submit-button">
            تأیید و ادامه
          </button>
          <button type="button" onClick={onPreviousStep} className="previous-button">
            بازگشت
          </button>
        </div>
      </form>
    </div>
  );
}

export default DetailsStep;
